import { useMemo } from 'react'
import type { JSX } from 'react'

import {
  Banner,
  Badge,
  Button,
  Flex,
  Heading,
  Separator,
  Text,
} from '@buffer-mono/popcorn'

import { useChannels } from '~publish/components/Composer/store/hooks/useChannels'

import type { ChannelMapping, ImportItem } from '../MigrationWizard/types'

type ReviewStepProps = {
  items: ImportItem[]
  channelMappings: ChannelMapping[]
  timezone: string
  onStartImport: () => void
  onBack: () => void
}

export function ReviewStep({
  items,
  channelMappings,
  timezone,
  onStartImport,
  onBack,
}: ReviewStepProps): JSX.Element {
  const { channels: channelsMap } = useChannels({ skip: false })

  const stats = useMemo(() => {
    const mappedChannelIds = new Set(
      channelMappings
        .filter((m) => m.mappedChannelId)
        .map((m) => m.mappedChannelId),
    )

    const mappedSourceKeys = new Set(
      channelMappings.filter((m) => m.mappedChannelId).map((m) => m.sourceKey),
    )

    const importable = items.filter(
      (i) =>
        i.status === 'valid' &&
        mappedSourceKeys.has(
          `${i.channel.sourcePlatform}|${i.channel.sourceAccountName}`,
        ),
    )

    const skippedUnmapped = items.filter(
      (i) =>
        i.status === 'valid' &&
        !mappedSourceKeys.has(
          `${i.channel.sourcePlatform}|${i.channel.sourceAccountName}`,
        ),
    )

    const parseErrors = items.filter((i) => i.status === 'error')

    const byStatus = {
      draft: 0,
      scheduled: 0,
      sent: 0,
      unknown: 0,
    }
    for (const item of importable) {
      byStatus[item.post.originalStatus]++
    }

    const withImages = importable.filter(
      (i) => i.post.media?.type === 'image',
    ).length
    const withVideo = importable.filter(
      (i) => i.post.media?.type === 'video',
    ).length

    // Per-channel breakdown
    const perChannel: Array<{
      channelId: string
      channelName: string
      service: string
      postCount: number
    }> = []

    for (const mapping of channelMappings) {
      if (!mapping.mappedChannelId) continue
      const channel = channelsMap[mapping.mappedChannelId]
      const postsForChannel = importable.filter(
        (i) =>
          `${i.channel.sourcePlatform}|${i.channel.sourceAccountName}` ===
          mapping.sourceKey,
      )
      perChannel.push({
        channelId: mapping.mappedChannelId,
        channelName: channel?.displayName || channel?.name || 'Unknown',
        service: channel?.service || mapping.sourcePlatform,
        postCount: postsForChannel.length,
      })
    }

    return {
      totalImportable: importable.length,
      skippedUnmapped: skippedUnmapped.length,
      parseErrors: parseErrors.length,
      byStatus,
      withImages,
      withVideo,
      perChannel,
      channelCount: mappedChannelIds.size,
    }
  }, [items, channelMappings, channelsMap])

  if (stats.totalImportable === 0) {
    return (
      <Flex direction="column" gap="lg">
        <Banner variant="warning">
          <Banner.Text>
            No posts are available to import.
            {stats.skippedUnmapped > 0 &&
              ` ${stats.skippedUnmapped} posts were skipped because their channels are not mapped.`}
            {stats.parseErrors > 0 &&
              ` ${stats.parseErrors} posts had parse errors.`}
            {stats.skippedUnmapped === 0 &&
              stats.parseErrors === 0 &&
              ' Make sure you have connected channels that match the ones in your export.'}
          </Banner.Text>
        </Banner>
        <Flex gap="sm" justify="end">
          <Button variant="secondary" onClick={onBack}>
            Back
          </Button>
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex direction="column" gap="lg">
      <Flex direction="column" gap="xs">
        <Heading size="small">
          Importing {stats.totalImportable.toLocaleString()} posts across{' '}
          {stats.channelCount} channel{stats.channelCount !== 1 ? 's' : ''}
        </Heading>
        <Text color="subtle">Timezone: {timezone}</Text>
      </Flex>

      {/* Per-channel breakdown */}
      <Flex direction="column" gap="sm" fullWidth>
        <Text weight="bold">Channel breakdown</Text>
        {stats.perChannel.map((ch) => (
          <Flex key={ch.channelId} align="center" justify="between" fullWidth>
            <Flex align="center" gap="xs">
              <Text>{ch.channelName}</Text>
              <Badge size="small">{ch.service}</Badge>
            </Flex>
            <Text color="subtle">
              {ch.postCount} post{ch.postCount !== 1 ? 's' : ''}
            </Text>
          </Flex>
        ))}
      </Flex>

      <Separator />

      {/* Status breakdown */}
      <Flex direction="column" gap="sm">
        <Text weight="bold">Post status breakdown</Text>
        <Flex gap="lg" wrap="wrap">
          {stats.byStatus.scheduled > 0 && (
            <Text color="subtle">{stats.byStatus.scheduled} scheduled</Text>
          )}
          {stats.byStatus.draft > 0 && (
            <Text color="subtle">{stats.byStatus.draft} drafts</Text>
          )}
          {stats.byStatus.sent > 0 && (
            <Text color="subtle">
              {stats.byStatus.sent} sent (will be imported as drafts)
            </Text>
          )}
          {stats.byStatus.unknown > 0 && (
            <Text color="subtle">
              {stats.byStatus.unknown} unknown status (will be imported as
              drafts)
            </Text>
          )}
        </Flex>
      </Flex>

      {/* Warnings */}
      {stats.withImages > 0 && (
        <Banner variant="info">
          <Banner.Text>
            {stats.withImages} post{stats.withImages !== 1 ? 's' : ''} with
            images — media will be imported automatically. If any image URLs
            have expired, those posts will be created as text-only.
          </Banner.Text>
        </Banner>
      )}

      {stats.withVideo > 0 && (
        <Banner variant="warning">
          <Banner.Text>
            {stats.withVideo} post{stats.withVideo !== 1 ? 's' : ''} with video
            — video import is not yet supported. These posts will be created as
            text-only.
          </Banner.Text>
        </Banner>
      )}

      {(stats.skippedUnmapped > 0 || stats.parseErrors > 0) && (
        <Banner variant="info">
          <Banner.Text>
            {stats.skippedUnmapped + stats.parseErrors} post
            {stats.skippedUnmapped + stats.parseErrors !== 1 ? 's' : ''} will be
            skipped
            {stats.skippedUnmapped > 0 &&
              ` (${stats.skippedUnmapped} from unmapped channels)`}
            {stats.parseErrors > 0 &&
              ` (${stats.parseErrors} with parse errors)`}
            .
          </Banner.Text>
        </Banner>
      )}

      <Flex gap="sm" justify="end">
        <Button variant="secondary" onClick={onBack}>
          Back
        </Button>
        <Button
          variant="primary"
          onClick={onStartImport}
          disabled={stats.totalImportable === 0}
        >
          Start Import
        </Button>
      </Flex>
    </Flex>
  )
}
